import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  Stack,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Button,
  Divider,
  useTheme,
  TextField,
  InputAdornment
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined"
import SearchIcon from "@mui/icons-material/Search"

interface MobileAutocompleteModalProps<T> {
  open: boolean
  onClose: () => void
  label?: string
  options: readonly T[]
  value: any
  multiple?: boolean
  getOptionLabel: (option: T) => string
  isOptionEqualToValue?: (option: T, value: T) => boolean
  onOptionSelect: (option: T) => void
  onDone: () => void
  onClear?: () => void
  palette: any
}

const MobileAutocompleteModal = <T,>({
  open,
  onClose,
  label,
  options,
  value,
  multiple,
  getOptionLabel,
  isOptionEqualToValue,
  onOptionSelect,
  onDone,
  onClear,
  palette
}: MobileAutocompleteModalProps<T>) => {
  const theme = useTheme()
  const [searchTerm, setSearchTerm] = useState("")
  const showSearch = options.length > 20

  // Reset search when modal opens/closes
  useEffect(() => {
    if (open) {
      setSearchTerm("")
    }
  }, [open])

  // Filter options based on search term
  const filteredOptions = searchTerm
    ? options.filter((option) => getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase()))
    : options

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableScrollLock
      fullScreen={true}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "0"
        }
      }}
    >
      <DialogContent
        sx={{
          py: 2,
          px: 0,
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Stack sx={{ flex: 1, overflow: "hidden" }}>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ px: 2, flex: 1, flexGrow: 0 }}>
            <IconButton
              onClick={onClose}
              sx={{
                color: "text.primary"
              }}
            >
              <CloseIcon fontSize="large" />
            </IconButton>
          </Stack>
          <Box sx={{ px: 1, flex: 1, flexGrow: 0 }}>
            <Typography variant="h5" sx={{ color: "#01294e", mb: 2, px: 2 }}>
              {label || "Select Option"}
            </Typography>
            {showSearch && (
              <TextField
                fullWidth
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            )}
          </Box>

          <Divider flexItem />

          <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
            <List sx={{ p: 0 }}>
              {filteredOptions.map((option, index) => {
                const isSelected = multiple
                  ? ((value as T[]) || []).some((item) => isOptionEqualToValue?.(item, option) ?? item === option)
                  : isOptionEqualToValue?.(value as T, option) ?? value === option

                // Create a stable key based on the option content
                const optionKey = `option-${getOptionLabel(option)}-${index}`

                return (
                  <ListItem key={optionKey} disablePadding>
                    <ListItemButton
                      onClick={() => onOptionSelect(option)}
                      sx={{
                        py: multiple ? 0 : 1,
                        px: 2,
                        backgroundColor: isSelected ? "rgba(0, 210, 255, 0.08)" : "transparent",
                        "&:hover": {
                          backgroundColor: isSelected ? "rgba(0, 210, 255, 0.04)" : "transparent"
                        }
                      }}
                    >
                      {multiple && (
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox
                            checked={isSelected}
                            checkedIcon={<CheckBoxOutlinedIcon />}
                            size="small"
                            sx={{
                              color: palette.action.disabled,
                              "&.Mui-checked": {
                                color: palette.primary.main
                              }
                            }}
                          />
                        </ListItemIcon>
                      )}
                      <ListItemText
                        primary={getOptionLabel(option)}
                        sx={{
                          my: 0,
                          "& .MuiListItemText-primary": {
                            fontWeight: isSelected ? 600 : 400,
                            color: isSelected ? "#01294e" : "inherit",
                            fontSize: "0.95rem",
                            lineHeight: 1.3
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                )
              })}
              {filteredOptions.length === 0 && (
                <ListItem>
                  <ListItemText primary="No options found" sx={{ textAlign: "center", color: "text.secondary" }} />
                </ListItem>
              )}
            </List>
          </Box>

          {multiple && (
            <Stack spacing={2} direction="row" sx={{ px: 1 }}>
              <Button variant="contained" fullWidth onClick={onDone} size="large">
                Done
              </Button>
              {onClear && (
                <Button variant="outlined" fullWidth onClick={onClear} size="large">
                  Clear Selection
                </Button>
              )}
            </Stack>
          )}
          {!multiple && onClear && (
            <Box>
              <Button variant="outlined" fullWidth onClick={onClear} size="large">
                Clear Selection
              </Button>
            </Box>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default MobileAutocompleteModal
